// A task's chat (D82): the task's own conversation, where its steps already wrote.
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";

import { ChatView } from "../chat/ChatView";
import { useT } from "../i18n";
import { detailCrumbs } from "../layout/nav";
import { taskGroupQuery, taskQuery } from "../tasks/api";
import { ApprovalCard } from "../tasks/chat/ApprovalCard";
import { refetchInterval, useLiveTasks } from "../tasks/live";
import { TaskStatusPill } from "../tasks/TaskStatusPill";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState } from "../ui/states";

export default function TaskChatPage() {
  const { shell, chat, tasks } = useT();
  const { taskId = "" } = useParams();
  const live = useLiveTasks({ tasks: [taskId] });
  const task = useQuery({ ...taskQuery(taskId), refetchInterval: refetchInterval(live) });
  const groupId = task.data?.task.task_group_id ?? "";
  const group = useQuery({ ...taskGroupQuery(groupId), enabled: groupId !== "" });
  const page = shell.pages.taskChat;
  const title = task.data?.task.title ?? "…";
  const crumbs = detailCrumbs(
    "taskGroups",
    "/task-groups",
    { name: group.data?.name ?? "…", href: groupId ? `/task-groups/${groupId}` : undefined },
    { name: title, href: `/tasks/${taskId}` },
    { name: page.title },
  );
  const conversationId = task.data ? (task.data.task.conversation_id ?? null) : undefined;
  // A step whose agent asked (S141): its questions are answered above the thread.
  const active = task.data?.task.active_step;
  const questionsRun = task.data?.task.waiting_on?.kind === "questions" && active?.kind === "prompt" ? active.run : undefined;
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="min-w-0 break-words">{`${page.title} · ${title}`}</span>
            {task.data ? <TaskStatusPill task={task.data.task} /> : null}
          </span>
        }
        breadcrumb={crumbs}
        description={page.subtitle}
      />
      {task.isError ? <ErrorState title={chat.loadFailed} message={task.error.message} /> : null}
      <ApprovalCard taskId={taskId} />
      {conversationId === null ? (
        <EmptyState compact title={tasks.task.noConversation}>
          {tasks.task.noConversationBody}
        </EmptyState>
      ) : null}
      {task.isError || conversationId === null ? null : <ChatView conversationId={conversationId} emptyBody={chat.emptyTask} waitHint={chat.turn.waitsForStep} title={page.title} questionsRun={questionsRun} />}
    </>
  );
}
